/**
 * App ⇄ parent-frame messaging, and the host-origin policy both directions
 * share.
 *
 * The app is embedded by exactly two hosts:
 *
 * - Figma's plugin UI iframe, which hosts `/tickets/figma-plugin`
 *   (`plugins/figma-puntt/src/ui.html`). That iframe is a null-origin
 *   document, so Figma's own origin never shows up as the app's immediate
 *   parent — only in the browser-reported chain above it, which is what
 *   `isFigmaPluginUiFrame` checks.
 * - The Puntt Chrome extension popup, which hosts `/tickets/chrome-extension`
 *   (`plugins/puntt-ai-reviews/src/popup.tsx`).
 *
 * What the policy enforces, in full:
 *
 * - `http(s)` embedders: only Figma's own https pages (`figma.com` and its
 *   subdomains) are named. Every other `http(s)` embedder gets nothing in
 *   either direction — no message out, and nothing it sends is acted on.
 * - `chrome-extension://<id>`: any extension that embeds the app has an origin
 *   the app posts to, so any extension that frames the app receives app UI
 *   state and can drive it wherever the chain decides (the bullet below governs
 *   a document that reports no chain). A Web Store id is derived from the
 *   signing key and is stable across installs, so it could be pinned here, but
 *   it is not recorded in this repo: the scheme is the allowlist. Wider than
 *   the Figma side, and unchanged by this policy.
 * - Deciding: the browser-reported `ancestorOrigins` chain decides, in both
 *   directions (`isExpectedParentChain`). A document that reports no chain —
 *   Firefox before 148, and engines that do not implement `ancestorOrigins`
 *   (`jsdom` in these tests, some webviews; Safari has reported it since 6 and
 *   Firefox since 148, so neither is in this population) — has nothing else to
 *   decide from but `document.referrer`, in both directions: a `figma.com`
 *   referrer is trusted, anything else gets nothing. The referrer is the
 *   embedding document there, so what the app sends is pinned to that origin;
 *   the broadcast target is reached on one path only, the opaque plugin-UI
 *   hop a chain reports.
 * - Where those version numbers come from: MDN's browser-compat data for
 *   `api.Location.ancestorOrigins`
 *   (developer.mozilla.org/docs/Web/API/Location/ancestorOrigins#browser_compatibility)
 *   — Chromium and WebKit have shipped it for years, Gecko only from Firefox
 *   148. That table is the entire basis for the claim; this repo records no
 *   minimum browser for the app or for the Figma plugin, so the population is
 *   bounded by a compat table rather than by a support decision of ours. If
 *   that is ever not good enough, the fix is to decide a minimum browser and
 *   cite it here — not to widen the policy.
 * - On that last population the Figma plugin is expected to be a no-op, and
 *   that is the accepted outcome of failing closed, not an oversight: inside
 *   Figma's plugin-UI frame the app's referrer is the `data:` document that
 *   frames it, which browsers strip to the empty string, so the plugin's
 *   resize, selection, upload-success and logout messaging requires the
 *   `ancestorOrigins` chain and cannot be reached without it. Measured, not
 *   assumed: `qa/rickrubin/tests/specs/cca/figma-plugin.parent-frame.spec.ts`
 *   reads both signals in the app frame in that chain. Every deny on that
 *   path is logged by `warnDenied`, so the no-op is not silent in a console.
 * - An unframed document (`window.parent === window`) is its own parent: what
 *   the app posts lands on itself, and a self-post is the only sender it
 *   accepts.
 */

/**
 * What the browser reports for an ancestor frame whose document has an opaque
 * origin — Figma's plugin UI, or any `sandbox`ed / `data:` document (see
 * below). The literal string is not a parseable origin, so it can only ever be
 * compared, never used as a target.
 */
const OPAQUE_ORIGIN = "null";

const CHROME_EXTENSION_SCHEME = "chrome-extension://";

/** True for a browser-reported origin the app is willing to post to. */
export function isExpectedParentOrigin(origin: string): boolean {
  return isFigmaOrigin(origin) || isChromeExtensionOrigin(origin);
}

/**
 * True for an https Figma origin: the apex domain or any subdomain.
 *
 * why this is wider than the CSP: `frameAncestors` in
 * `frontends/carrie/cspConfig.ts` names only `https://www.figma.com`. That list
 * is the browser-level framing allowlist and this is the message policy, so the
 * two are deliberately not identical — but they do have to move together, and
 * widening one without the other yields an embedder that can frame the app but
 * is sent nothing, or the reverse.
 */
export function isFigmaOrigin(origin: string): boolean {
  try {
    const { protocol, hostname } = new URL(origin);
    return (
      protocol === "https:" &&
      (hostname === "figma.com" || hostname.endsWith(".figma.com"))
    );
  } catch {
    return false;
  }
}

function isChromeExtensionOrigin(origin: string): boolean {
  return (
    origin.startsWith(CHROME_EXTENSION_SCHEME) &&
    origin.length > CHROME_EXTENSION_SCHEME.length
  );
}

/**
 * True when the browser-reported chain names an embedder the app talks to:
 * either the parent origin itself, or Figma's opaque plugin-UI frame with
 * Figma's own pages above it.
 *
 * Both directions decide from this one function, so a host family or an
 * opaque-frame rule added for what the app sends cannot be missing from what
 * it accepts — the two used to spell the same two-call expression out by hand,
 * and no test could see them drift apart.
 */
export function isExpectedParentChain(
  parentOrigin: string,
  ancestorsAbove: string[],
): boolean {
  return (
    isExpectedParentOrigin(parentOrigin) ||
    isFigmaPluginUiFrame(parentOrigin, ancestorsAbove)
  );
}

/**
 * True when the document was reached from a Figma page — the only signal
 * available on a browser that reports no `ancestorOrigins` (see the module
 * docstring).
 */
export function hasFigmaReferrer(): boolean {
  return isFigmaOrigin(referrerOrigin());
}

/**
 * Post `message` up to the frame that embeds the app.
 *
 * Returns `false` without posting when the parent frame is not an expected
 * embedder.
 */
export function postToParent(message: unknown): boolean {
  // Not framed: `window.parent` is this window, so the app is the only
  // receiver. Post to our own origin instead of broadcasting.
  if (window.parent === window) {
    window.parent.postMessage(message, window.location.origin);
    return true;
  }

  const ancestors = ancestorOrigins();

  if (ancestors && ancestors.length > 0) {
    const [parentOrigin, ...ancestorsAbove] = ancestors;

    if (!isExpectedParentChain(parentOrigin, ancestorsAbove)) {
      warnDenied(
        "postToParent",
        "chain is not an expected embedder",
        ancestors,
      );
      return false;
    }

    // A nameable parent gets its own origin. The only hop the chain check
    // leaves unnameable is Figma's opaque plugin-UI frame, which is where the
    // broadcast target is the one value a browser delivers on.
    if (isExpectedParentOrigin(parentOrigin)) {
      window.parent.postMessage(message, parentOrigin);
    } else {
      postToFigmaPluginUi(message);
    }
    return true;
  }

  // A browser that reports no chain (see the module docstring): the referrer
  // is the only signal there is. For a framed document the referrer is the
  // embedding document, so the guard below has already named this parent —
  // post to that origin rather than broadcasting to whatever the frame holds
  // by the time the message lands.
  if (hasFigmaReferrer()) {
    window.parent.postMessage(message, referrerOrigin());
    return true;
  }

  warnDenied("postToParent", "no chain and no Figma referrer", ancestors);
  return false;
}

/**
 * True when `event` came from the frame that embeds the app.
 *
 * why: pinning the target origin of what the app sends is only half the
 * boundary — without this an unexpected embedder can still drive the app, and
 * an `EXPORT_RESULT` runs an upload plus the ticket and revision mutations.
 * `event.source` is the check that works for both hosts: Figma's plugin UI
 * frame is opaque, so its origin cannot be named, but it is still
 * `window.parent`.
 */
export function isFromExpectedParent(event: MessageEvent): boolean {
  if (event.source !== window.parent) {
    warnDenied(
      "isFromExpectedParent",
      "sender is not the embedding frame",
      ancestorOrigins(),
    );
    return false;
  }

  // Where the sender names an origin, it has to be one the app talks to. This
  // is the check that still bites on browsers which report no ancestor chain
  // (see the module docstring), where the sender's origin is the only nameable
  // thing in the message. Figma's plugin UI frame is opaque and reports
  // `"null"`, which is why the source check above is the one that carries that
  // hop. The app's own origin is exempt because it is a sender the app has:
  // `postToParent` posts to `window.location.origin` on its unframed branch,
  // so denying it here would deny the self-post this module accepts.
  if (
    event.origin &&
    event.origin !== OPAQUE_ORIGIN &&
    event.origin !== window.location.origin &&
    !isExpectedParentOrigin(event.origin)
  ) {
    warnDenied(
      "isFromExpectedParent",
      "sender names an origin the app does not talk to",
      ancestorOrigins(),
    );
    return false;
  }

  const ancestors = ancestorOrigins();

  if (!ancestors || ancestors.length === 0) {
    // No chain to decide from: either an unframed document, whose only sender
    // is itself, or a browser that reports no chain (see the module
    // docstring), where the referrer is the signal — the same one the outbound
    // path uses, so the two directions answer alike. An opaque sender
    // (`origin === "null"`), which is how any sandboxed or `data:` frame
    // reports itself, passes the origin clause above by construction, so this
    // is the arm that has to decide about it.
    const expected = window.parent === window || hasFigmaReferrer();

    if (!expected) {
      warnDenied(
        "isFromExpectedParent",
        "no chain and no Figma referrer",
        ancestors,
      );
    }
    return expected;
  }

  const [parentOrigin, ...ancestorsAbove] = ancestors;
  const expected = isExpectedParentChain(parentOrigin, ancestorsAbove);

  if (!expected) {
    warnDenied(
      "isFromExpectedParent",
      "chain is not an expected embedder",
      ancestors,
    );
  }
  return expected;
}

/**
 * One line on every deny path.
 *
 * why: a deny used to be silent, and all eight call sites drop the return
 * value, so a host whose chain is not the one this module was measured against
 * was indistinguishable in production from "there was nothing to send" — and
 * the failure this policy can cause (resize, selection, upload-success and
 * logout not reaching Figma) is exactly that. The line names the direction, the
 * reason, the observed chain, whether the document is framed, and the
 * referrer's origin — origins only, no paths and no query strings, since a
 * referrer URL is not this module's business to log.
 */
function warnDenied(
  direction: "postToParent" | "isFromExpectedParent",
  reason: string,
  ancestors: string[] | null,
): void {
  console.warn(
    `[parent-messaging] ${direction} denied (${reason}): ancestorOrigins=${JSON.stringify(ancestors)} referrerOrigin=${JSON.stringify(referrerOrigin())} framed=${window.parent !== window}`,
  );
}

/**
 * `window.location.ancestorOrigins`, nearest ancestor first. `null` when the
 * browser does not implement it (Firefox before 148, jsdom and other engines
 * that never added it); empty when the app is not framed.
 */
function ancestorOrigins(): string[] | null {
  const origins = (window.location as { ancestorOrigins?: DOMStringList })
    .ancestorOrigins;

  return origins ? Array.from(origins) : null;
}

function referrerOrigin(): string {
  try {
    return new URL(document.referrer).origin;
  } catch {
    // Empty or unparseable referrer — not an origin we can name.
    return "";
  }
}

/**
 * The app sits in a null-origin iframe inside Figma: a plugin UI that "has not
 * navigated the iframe to a custom URL" lives in one
 * (developers.figma.com/docs/plugins/creating-ui), and `ui.html` embeds the
 * app that way.
 *
 * why: a browser only delivers a message to an opaque-origin window for the
 * broadcast target origin, and any other value — including the string
 * `"null"` — either fails the origin check or throws a `SyntaxError`. So this
 * one hop cannot be pinned. The ancestor origins are browser-reported, and an
 * embedder cannot put an opaque frame under a figma.com document, so requiring
 * Figma above the opaque frame is what keeps an unexpected embedder out.
 *
 * Its only caller is `postToParent`'s chain branch, after
 * `isFigmaPluginUiFrame` has placed Figma's own pages above that opaque
 * parent. The chainless branch is not this case: there the referrer names the
 * embedding document, so that post is pinned to it.
 */
function postToFigmaPluginUi(message: unknown): void {
  window.parent.postMessage(message, "*");
}

/**
 * True when the parent frame is that null-origin plugin UI iframe: the
 * immediate parent is opaque and every frame above it is Figma's or another
 * opaque frame in that chain.
 *
 * why there is no separate "is anything reported above?" test: `some` is false
 * for an empty chain, so `["null"]` — an opaque parent with nothing reported
 * above it, which is what a stranger's sandboxed frame produces — is already
 * rejected by the `.some` below. A length check on top of it could never
 * change the answer.
 */
function isFigmaPluginUiFrame(
  parentOrigin: string,
  ancestorsAbove: string[],
): boolean {
  return (
    parentOrigin === OPAQUE_ORIGIN &&
    ancestorsAbove.some(isFigmaOrigin) &&
    ancestorsAbove.every(
      (origin) => isFigmaOrigin(origin) || origin === OPAQUE_ORIGIN,
    )
  );
}
